// web/colonyPower.ts — COLONY POWER RECORDS: a PURE, DISPLAY-ONLY read-model that
// distils each CASTE's resolved profile into one comparable integer "power", plus the
// all-time-high bookkeeping behind the live "NEW COLONY RECORD" banners.
//
// WHAT IT IS: a single legible per-CASTE power number (Worker/Soldier/Flyer/Specialist)
// and their sum (Colony Power), so the moment the player applies a gene that lifts a
// caste — or the colony as a whole — past its previous best, the UI can celebrate it.
// This is the per-CASTE / colony counterpart to src/sim/genePower.ts (which scores a
// single GENE); the two are deliberately separate read-models over different inputs.
//
// CRITICAL — this is NOT a tick System, NOT sim state, NOT save/replay state
// (mirrors src/sim/genePower.ts and the render-only layers in DESIGN.md §19/§24):
//   • It NEVER mutates the genome, the GameSession, or any sim state.
//   • It NEVER draws RNG — every function here is a deterministic pure function of
//     integer CasteProfile fields.
//   • It is NOT folded into any state hash and is NOT part of the deterministic save
//     payload. The records live in their OWN localStorage key (RECORDS_KEY), wholly
//     separate from the save (web/save.ts) and the player profile (web/profile.ts),
//     so they cannot perturb determinism, the action log, or replay.
//   • localStorage is touched LAZILY (guarded by `typeof localStorage`), so the module
//     imports cleanly in a headless test with no DOM.
//
// ALLOCATION: the scoring functions allocate nothing beyond the small result objects
// the record helpers return (these run on a player action, never in the tick hot path).

import type { CasteProfile } from '../src/sim/genome.ts';
import { CASTE } from '../src/types.ts';

// ---------------------------------------------------------------------------
// Per-caste power scoring (pure; directly unit-testable).
// ---------------------------------------------------------------------------
//
// Weights are the per-unit contribution of each CasteProfile field. They are chosen so
// the score is STRICTLY MONOTONIC in every field that can grow a profile's strength:
// raising any single weighted field (and nothing else) strictly raises the score. That
// monotonicity is the contract the banners rely on (a strictly stronger caste must read
// as a record) and is asserted in colonyPower.test.mjs.

/** Per point of attack power — the dominant combat term. */
export const POWER_ATTACK = 4;
/** Per hit point of starting/max HP (survivability). */
export const POWER_HP = 2;
/** Per movement step/tick — mobility is highly valued (and rare to raise). */
export const POWER_STEPS = 8;
/** Per tile of sense radius (awareness). */
export const POWER_SENSE = 2;
/** Per unit of carry capacity (logistics throughput). */
export const POWER_CARRY = 2;
/** Per hp/tick regenerated (sustained survivability). */
export const POWER_REGEN = 6;
/** Per point of toxic potency. */
export const POWER_TOXICITY = 3;
/** Per point of size scalar. */
export const POWER_SIZE = 2;

/**
 * A caste's single Power Score — a deterministic integer weighted sum emphasising
 * combat + capability. Each term is `weight * max(0, field)` so a clamped-to-zero field
 * never drags the score negative, and every term is independently monotonic non-
 * decreasing in its field. Pure integer arithmetic in a fixed term order.
 */
export function casteProfilePower(p: CasteProfile): number {
  const attack = POWER_ATTACK * Math.max(0, p.attackPower);
  const hp = POWER_HP * Math.max(0, p.startHp);
  const steps = POWER_STEPS * Math.max(0, p.stepsPerTick);
  const sense = POWER_SENSE * Math.max(0, p.senseRadius);
  const carry = POWER_CARRY * Math.max(0, p.carryCapacity);
  const regen = POWER_REGEN * Math.max(0, p.regen);
  const toxicity = POWER_TOXICITY * Math.max(0, p.toxicity);
  const size = POWER_SIZE * Math.max(0, p.size);
  return (attack + hp + steps + sense + carry + regen + toxicity + size) | 0;
}

// ---------------------------------------------------------------------------
// Colony power snapshot + records.
// ---------------------------------------------------------------------------

/** The four caste powers of a single moment, plus the colony total (their sum). */
export interface PowerSnapshot {
  worker: number;
  soldier: number;
  flyer: number;
  specialist: number;
  colony: number;
}

/** Colony Power = the sum of the four caste powers. Pure integer. */
export function colonyPower(
  worker: number,
  soldier: number,
  flyer: number,
  specialist: number,
): number {
  return (worker + soldier + flyer + specialist) | 0;
}

// ---------------------------------------------------------------------------
// Diminishing returns — the DISPLAY curve. Raw power (casteProfilePower) keeps growing
// with the genome so the apply budget + adaptive-loot need vector stay raw and byte-
// identical; but the power the PLAYER reads grows with DIMINISHING returns, so early gains
// feel major (100→200) while late ones feel minor (5000→5100) — and progression is still
// infinite (the curve is monotonic + UNBOUNDED, never clamped to a ceiling). Mirrors the
// soft-cap idiom (genes.softCapChannel / leaderboard.softCapSubscore) but without a cap.
// ---------------------------------------------------------------------------

/** Per-caste raw power below which the display is 1:1 (early game stays punchy); above it
 *  the curve compresses logarithmically. Tunable. */
export const DIMINISH_SCALE = 500;

/**
 * Map a raw power to its displayed "effective" power: identity up to `scale`, then
 * `scale + scale·ln(raw/scale)` above it — smooth, monotonic, UNBOUNDED (slope 1 at
 * `scale`, →0 as raw→∞, but always rising, so power never stops climbing). Pure integer.
 * e.g. scale 500: 200→200, 1250→~958 — a +50 raw step is large at 200, small at 1250.
 */
export function effectivePower(raw: number, scale: number = DIMINISH_SCALE): number {
  if (raw <= 0) return 0;
  if (raw <= scale) return raw | 0;
  return Math.round(scale + scale * Math.log(raw / scale));
}

/**
 * The displayed snapshot: each caste's effective power, with the colony as the SUM of the
 * effective castes — so the shown parts always add up to the shown whole (concavity makes
 * effectivePower(Σ) ≠ Σ effectivePower, so the colony total must be the sum). Accepts any
 * {worker,soldier,flyer,specialist} (a live snapshot or a stored record); its raw `colony`
 * is ignored and recomputed. Pure.
 */
export function effectiveSnapshot(
  s: { worker: number; soldier: number; flyer: number; specialist: number },
  scale: number = DIMINISH_SCALE,
): PowerSnapshot {
  const worker = effectivePower(s.worker, scale);
  const soldier = effectivePower(s.soldier, scale);
  const flyer = effectivePower(s.flyer, scale);
  const specialist = effectivePower(s.specialist, scale);
  return { worker, soldier, flyer, specialist, colony: (worker + soldier + flyer + specialist) | 0 };
}

/** The persisted all-time-high power per metric (same shape as a snapshot's fields). */
export interface PowerRecords {
  worker: number;
  soldier: number;
  flyer: number;
  specialist: number;
  colony: number;
}

/** One broken record: which metric, its human label, the new value and the old best.
 *  `key` is a PowerRecords/PowerSnapshot field name, so callers can index a snapshot with
 *  it directly (e.g. to show the hit through the diminishing-returns display curve). */
export interface RecordHit {
  key: keyof PowerRecords;
  label: string;
  value: number;
  prev: number;
}

/**
 * The metrics in their STABLE reporting order (worker → soldier → flyer → specialist →
 * colony). `key` matches the field name on PowerRecords / PowerSnapshot; `label` is the
 * banner text. Indexed by CASTE.* for the four castes (CASTE.Worker===0 … Specialist===3),
 * with the colony total appended last.
 */
interface Metric { key: keyof PowerRecords; label: string; }
const METRICS: readonly Metric[] = [
  { key: 'worker', label: 'Highest Worker Power Ever' },
  { key: 'soldier', label: 'Highest Soldier Power Ever' },
  { key: 'flyer', label: 'Highest Flyer Power Ever' },
  { key: 'specialist', label: 'Highest Specialist Power Ever' },
  { key: 'colony', label: 'Highest Colony Power Ever' },
];

// Compile-time guard: the first four metrics line up with the CASTE indices, so the
// snapshot fields and the labels can never silently drift out of order.
const _CASTE_ORDER_GUARD: readonly [0, 1, 2, 3] = [
  CASTE.Worker, CASTE.Soldier, CASTE.Flyer, CASTE.Specialist,
];
void _CASTE_ORDER_GUARD;

/**
 * Compare a fresh snapshot against the stored records and return one hit per metric
 * that STRICTLY exceeds its previous best, in the stable METRICS order. PURE — it reads
 * both inputs and mutates neither. An equal or lower value produces no hit (so re-
 * applying the same gene never re-fires a banner).
 */
export function checkRecords(prev: PowerRecords, cur: PowerSnapshot): RecordHit[] {
  const hits: RecordHit[] = [];
  for (const m of METRICS) {
    const value = cur[m.key];
    const before = prev[m.key];
    if (value > before) hits.push({ key: m.key, label: m.label, value, prev: before });
  }
  return hits;
}

/**
 * Element-wise max of the stored records and a fresh snapshot — the new running bests.
 * PURE: returns a NEW object and mutates neither input.
 */
export function mergeRecords(prev: PowerRecords, cur: PowerSnapshot): PowerRecords {
  return {
    worker: Math.max(prev.worker, cur.worker),
    soldier: Math.max(prev.soldier, cur.soldier),
    flyer: Math.max(prev.flyer, cur.flyer),
    specialist: Math.max(prev.specialist, cur.specialist),
    colony: Math.max(prev.colony, cur.colony),
  };
}

/** The zero-baseline records — every metric at 0 (used to seed on the first apply). */
export function zeroRecords(): PowerRecords {
  return { worker: 0, soldier: 0, flyer: 0, specialist: 0, colony: 0 };
}

// ---------------------------------------------------------------------------
// Isolated persistence — its OWN localStorage key, separate from the save and the
// player profile. localStorage is touched lazily and guarded, so importing this module
// (e.g. in a headless test) never touches the DOM and never throws.
// ---------------------------------------------------------------------------

const RECORDS_KEY = 'brood.records.v1';

/** True only when a usable localStorage exists (guards headless test / SSR import). */
function hasStorage(): boolean {
  return typeof localStorage !== 'undefined' && localStorage !== null;
}

/** All five fields present and finite ⇒ a structurally valid PowerRecords. */
function isValidRecords(v: unknown): v is PowerRecords {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  for (const k of ['worker', 'soldier', 'flyer', 'specialist', 'colony']) {
    if (typeof r[k] !== 'number' || !Number.isFinite(r[k] as number)) return false;
  }
  return true;
}

/**
 * Load the stored records, or null when absent / unparseable / structurally invalid /
 * no localStorage. A null return is the caller's "first ever — seed silently" signal.
 */
export function loadRecords(): PowerRecords | null {
  if (!hasStorage()) return null;
  let raw: string | null;
  try { raw = localStorage.getItem(RECORDS_KEY); } catch { return null; }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isValidRecords(parsed)) return null;
    return {
      worker: parsed.worker,
      soldier: parsed.soldier,
      flyer: parsed.flyer,
      specialist: parsed.specialist,
      colony: parsed.colony,
    };
  } catch {
    return null;
  }
}

/** Persist the records, best-effort. Swallows quota / disabled / serialise errors. */
export function saveRecords(r: PowerRecords): void {
  if (!hasStorage()) return;
  try { localStorage.setItem(RECORDS_KEY, JSON.stringify(r)); } catch { /* ignore */ }
}
